import { maxBy, minBy } from 'lodash';
import { getRotatePosition } from '@/utils/collision-kit';
import { getLayerRuntimeValue } from '@/utils/layer-kit';
import {
    MarkupTextPositionType,
    MarkupType,
    type ILayerProps,
    type IMarkupLabel,
    type IMarkupLayerProps,
    type IPoint,
    type ISize,
} from '@/version-types';
import { createMarkupLabelLayout, getMarkupLabelRenderText, type IMarkupLabelLayout } from './label-layout';
import { clampMarkupLabelRoundWeight } from './round-weight';
import { MARKUP_POINT_SIZE } from './size';

class LabelKit {
    private minFontSize = 12;

    static getLabelSize(layer: ILayerProps) {
        const { label } = layer.data as IMarkupLayerProps;
        const { paddingH, paddingV } = LabelKit.getPadding(layer);
        if (!LabelKit.getRenderText(label)) return { width: 0, height: 0, paddingH, paddingV };
        // console.log({ paddingH, paddingV, width: label.width, height: label.height });
        // 加上背景的padding，文字区域的真实宽度
        const width = label.width + paddingH * 2;
        const height = label.height + paddingV * 2;
        return {
            width,
            height,
            paddingH,
            paddingV,
        };
    }

    static getPadding(layer: ILayerProps, fontSize?: number) {
        const { label } = layer.data as IMarkupLayerProps;
        if (!LabelKit.getRenderText(label)) return { paddingH: 0, paddingV: 0 };
        const markupType = getLayerRuntimeValue<MarkupType>(layer, 'markupType');
        const baseFontSize = markupType === MarkupType.Segment ? 137 : 103;
        // 字号为 103 大小时，背景的左右 padding 比例
        const paddingHRatio = 70 / baseFontSize;
        const paddingH = Math.floor(label.background ? paddingHRatio * (fontSize || label.fontSize) : 0);
        // 背景的上下 padding 比例
        let paddingVRatio = 10 / baseFontSize;
        if (markupType === MarkupType.Segment) {
            paddingVRatio = 28 / baseFontSize;
        }
        const paddingV = Math.floor(label.background ? paddingVRatio * (fontSize || label.fontSize) : 0);
        return {
            paddingH,
            paddingV,
        };
    }

    static getRoundWeight(layer: ILayerProps) {
        const { label } = layer.data as IMarkupLayerProps;
        if (!label.background) return 0;
        let roundWeight = label.height;
        const markupType = getLayerRuntimeValue<MarkupType>(layer, 'markupType');
        if (markupType === MarkupType.Segment) {
            // roundWeight = Math.ceil((45 / 208) * label.height);
            // 减去上下 padding 32
            roundWeight = Math.ceil((45 / (208 - 32)) * label.height);
        }
        return clampMarkupLabelRoundWeight(roundWeight, LabelKit.getLabelSize(layer).height);
    }

    static updateRoundWeight(layer: ILayerProps) {
        const { label } = layer.data as IMarkupLayerProps;
        label.roundWeight = LabelKit.getRoundWeight(layer);
        return label.roundWeight;
    }

    static getMargin(layer: ILayerProps, width: number, fontSize?: number, fillWidth = 0) {
        const { label } = layer.data as IMarkupLayerProps;
        if (!LabelKit.getRenderText(label)) return { marginH: 0, marginV: 0 };
        const markupType = getLayerRuntimeValue<MarkupType>(layer, 'markupType');

        // 尺寸标注， 1000px 宽度，margin 为 24px
        let marginRatioH = 24 / 1000;
        let marginRatioV = 24 / 1000;
        // 指引线，1000px 宽度，margin 有背景 49px，无背景 68px
        let offsetMarginH = 0;
        if (markupType === MarkupType.Segment) {
            marginRatioH = (label.background ? 49 : 68) / 1000;
            marginRatioV = (label.background ? 49 : 68) / 1000;
            offsetMarginH = fillWidth / 2;
        }
        const marginH = Math.floor(width * marginRatioH);
        const marginV = Math.floor(width * marginRatioV);
        // lineHeight 的上下间隙
        const baseFontSize = markupType === MarkupType.Segment ? 137 : 103;
        const offsetPadding = Math.floor(label.background ? 0 : (16 / baseFontSize) * (fontSize || label.fontSize));

        return {
            marginH: marginH + offsetMarginH,
            marginV: marginV - offsetPadding,
        };
    }

    static getLabelRenderInfoByLayer(layer: ILayerProps) {
        const { markupType, fill, anchor, endPoint } = layer.data as IMarkupLayerProps;
        if (markupType === MarkupType.Segment) {
            const points = [anchor.points![0].x, 0];
            if (anchor.points![1]) {
                points.push(anchor.points![1].x);
            }
            const min = Math.min(...points);
            const max = Math.max(...points);
            let offset = 0;
            if (anchor.points![1] && endPoint.url) {
                // 计算从端点的边缘开始计算间距
                const scale = fill.width / MARKUP_POINT_SIZE;
                const height = endPoint.height * scale;
                offset = height / 2;
            }
            return LabelKit.getLabelRenderInfo(max - min, fill.width, layer, offset);
        }
        return LabelKit.getLabelRenderInfo(layer.width, fill.width, layer);
    }

    static getLabelRenderInfo(layerWidth: number, fillWidth: number, layer: ILayerProps, renderPositionOffset = 0) {
        const { label } = layer.data as IMarkupLayerProps;
        const { rotate } = label;
        const { width: labelWidth, height: labelHeight, paddingH, paddingV } = LabelKit.getLabelSize(layer);
        const { marginH, marginV } = LabelKit.getMargin(layer, layerWidth, undefined, fillWidth);
        // console.log({ labelWidth, labelHeight });
        const positions = getRotatePosition({
            left: 0,
            top: 0,
            width: labelWidth,
            height: labelHeight,
            rotate,
        });
        const minX = minBy(positions, (pos) => pos.x) as IPoint;
        const maxX = maxBy(positions, (pos) => pos.x) as IPoint;
        const minY = minBy(positions, (pos) => pos.y) as IPoint;
        const maxY = maxBy(positions, (pos) => pos.y) as IPoint;

        const renderWidth = maxX.x - minX.x;
        const renderHeight = maxY.y - minY.y;
        const halfRenderHeight = renderHeight / 2;

        const offsetMargin = (renderPositionOffset || fillWidth / 2) + marginV;
        let renderPosition = offsetMargin;
        if (label.rotate) {
            renderPosition += halfRenderHeight - labelHeight / 2;
        }

        let renderAreaUp = 0;
        let renderAreaDown = 0;
        if (label.position === MarkupTextPositionType.Center) {
            renderAreaUp = Math.ceil(halfRenderHeight);
            renderAreaDown = -Math.ceil(halfRenderHeight);
        } else if (label.position === MarkupTextPositionType.Up) {
            renderAreaUp = Math.ceil(renderHeight + offsetMargin);
        } else if (label.position === MarkupTextPositionType.Down) {
            renderAreaDown = -Math.ceil(renderHeight + offsetMargin);
        }

        return {
            /** 真实宽，文字宽 + 加上 padding */
            labelWidth,
            /** 真实高，文字高 + 加上 padding */
            labelHeight,
            /** 渲染宽，真实宽 + 加上 rotate */
            renderWidth,
            /** 渲染高，真实高 + 加上 rotate */
            renderHeight,
            /** 水平 padding */
            paddingH,
            /** 垂直 padding */
            paddingV,
            /** 水平 margin */
            marginH,
            /** 垂直 margin */
            marginV,
            /** 垂直定位，top bottom 的定位值 */
            renderPosition,
            /** 渲染区域 */
            renderArea: {
                /** 渲染占宽，渲染宽 + 双边 margin */
                width: renderWidth + marginH * 2,
                /** 渲染占上部分高 */
                up: renderAreaUp,
                /** 渲染占下部分高 */
                down: renderAreaDown,
            },
        };
    }

    static getRenderText(label: IMarkupLabel) {
        return getMarkupLabelRenderText(label);
    }

    getFontSize(layer: ILayerProps, fontSize: number, width: number): (ISize & { fontSize: number }) | null {
        if (fontSize <= this.minFontSize) {
            fontSize = this.minFontSize;
        }
        const layout = this.getLayout(layer, fontSize);
        if (!layout) return null;
        const size = { width: layout.width, height: layout.height };
        // console.log('getFontSize => ', fontSize, size);
        const { paddingH } = LabelKit.getPadding(layer, fontSize);
        const labelWidth = size.width + paddingH * 2;
        if (labelWidth <= width || fontSize === this.minFontSize) {
            return {
                fontSize,
                ...size,
            };
        }
        return this.getFontSize(layer, Math.max(fontSize - 1, this.minFontSize), width);
    }

    getLayout(layer: ILayerProps, fontSize?: number): IMarkupLabelLayout | null {
        return createMarkupLabelLayout(layer, fontSize);
    }

    getSize(layer: ILayerProps, fontSize?: number): ISize | null {
        const layout = this.getLayout(layer, fontSize);
        if (!layout) return null;
        return { width: layout.width, height: layout.height };
    }
}

export default LabelKit;
